import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ConversationMessage } from '../features/chat/types/conversation';
import { normalizeFilename } from '../utils/fileNameUtils';
import { getMessageCountStatus } from '../utils/messageUtils';
import { DEFAULT_MODEL } from '../config/defaults';
import { normalizeSelectedModel } from '../config/models';

/**
 * Simplified chat store focused on client-only state management
 * Server state (conversations, messages) moved to TanStack Query
 */

// Enhanced attachment interface for file uploads
interface ChatAttachment {
  id: string;
  file: File;
  uploadStatus: 'pending' | 'uploading' | 'completed' | 'error';
  stagingState: 'staged-for-upload' | 'none'; // New files default to staged-for-upload
  uploadProgress?: number;
  uploadSessionId?: string; // Link to backend session
  url?: string;
  error?: string;
}

// Home screen conversation interface
interface HomeScreenConversation {
  id: string;
  userMessage: ConversationMessage;
  assistantMessage?: ConversationMessage;
  error?: string;
  createdAt: Date;
}

// Conversation-level file state for persistence across modal opens/closes
interface ConversationFileEntry {
  name: string;
  status: 'queued' | 'uploading' | 'uploaded' | 'deleting' | 'error';
  origin?: 'inline' | 'modal';
  errorMessage?: string;
}

interface ConversationFileState {
  conversationId: string;
  uploadedFiles: string[]; // Files confirmed uploaded to server
  pendingFiles: ChatAttachment[]; // Files currently in upload process
  pendingForMessage: string[]; // Files pending to be sent with next message (for input indicators)
  fileEntries?: ConversationFileEntry[]; // Unified file entries with status
  lastUploadSessionId?: string; // Last upload session ID for this conversation
  lastSyncTimestamp?: number; // When this state was last synced with server
}

// Conversation input state interface
interface ConversationInputState {
  inputValue: string;
  selectedAgents: string[];
  selectedModel: string;
  characterCount: number;
  messageCount: number;  // Track message count per conversation
}

interface DigitalMaintenanceCheckState {
  eligibleAtMs: number;
  latestMessageId: string;
  buttonMessageId?: string;
}

interface ChatState {
  // Global input state (for backward compatibility, will be deprecated)
  inputValue: string;
  selectedAgents: string[];
  selectedModel: string;

  // Conversation-specific input state
  conversationInputs: Record<string, ConversationInputState>;
  digitalMaintenanceChecks: Record<string, DigitalMaintenanceCheckState>;
  setDigitalMaintenanceCheck: (conversationId: string, check: DigitalMaintenanceCheckState | undefined) => void;

  attachments: ChatAttachment[];

  // Upload session state
  uploadSessionId?: string; // Store session ID for message sending

  // Conversation-level file tracking
  conversationFiles: Record<string, ConversationFileState>;

  // Home screen conversation state
  homeScreenConversation: HomeScreenConversation | null;

  // Genie access state
  hasGenieAccess: boolean;
  allowedGenieSpaceNames: string[];

  // UI state
  isComposing: boolean;

  // Message editing state
  isEditingMessage: boolean;
  currentEditingMessageId: string | null;

  // Global AbortController for active edit request
  editAbortController: AbortController | null;

  // Genie access actions
  setGenieAccess: (hasAccess: boolean) => void;
  setAllowedGenieSpaceNames: (spaceNames: string[]) => void;

  // Modal state
  isAgentsModalOpen: boolean;
  isAtAgentsModalOpen: boolean;
  isModelsModalOpen: boolean;
  isPromptLibraryModalOpen: boolean;
  isAttachModalOpen: boolean;
  isProfileModalOpen: boolean;

  // Input actions (conversation-specific)
  setInputValue: (conversationId: string, value: string) => void;
  setSelectedAgent: (conversationId: string, agent: string) => void;
  setSelectedAgents: (conversationId: string, agents: string[]) => void;
  addSelectedAgent: (conversationId: string, agent: string) => void;
  removeSelectedAgent: (conversationId: string, agent: string) => void;
  setSelectedModel: (conversationId: string, model: string) => void;
  clearInput: (conversationId: string) => void;

  // Getter actions for conversation-specific state
  getInputValue: (conversationId: string) => string;
  getSelectedAgents: (conversationId: string) => string[];
  getSelectedModel: (conversationId: string) => string;

  // Character count actions
  getCharacterCount: (conversationId: string) => number;
  setCharacterCount: (conversationId: string, count: number) => void;
  isCharacterCountVisible: (conversationId: string) => boolean;
  isCharacterLimitExceeded: (conversationId: string) => boolean;

  // Message count actions
  getMessageCount: (conversationId: string) => number;
  setMessageCount: (conversationId: string, count: number) => void;
  isMessageCountVisible: (conversationId: string) => boolean;
  isMessageLimitWarning: (conversationId: string) => boolean;
  isMessageLimitReached: (conversationId: string) => boolean;
  getMessageCountStatus: (conversationId: string) => ReturnType<typeof getMessageCountStatus>;

  addAttachment: (file: File, conversationId?: string) => void;
  removeAttachment: (id: string) => void;
  updateAttachmentStatus: (id: string, status: ChatAttachment['uploadStatus'], progress?: number) => void;
  clearAttachments: () => void;

  // Upload session management
  setUploadSessionId: (sessionId: string) => void;
  clearUploadSession: () => void;
  markAttachmentsAsUploaded: (sessionId: string) => void;
  markAttachmentAsUploading: (fileId: string) => void;
  markAttachmentAsError: (fileId: string, error: string) => void;

  // Staging state management
  setStagingState: (fileId: string, state: 'staged-for-upload' | 'none') => void;
  getStagedForUpload: () => ChatAttachment[];
  hasStagedUploads: () => boolean;

  // Conversation-level file management
  addConversationFile: (conversationId: string, filename: string) => void;
  removeConversationFile: (conversationId: string, filename: string) => void;
  getConversationFiles: (conversationId: string) => string[];
  // New unified entries API
  addOrQueueFile: (conversationId: string, filename: string, origin?: 'inline' | 'modal') => void;
  markFileUploading: (conversationId: string, filename: string) => void;
  markFileUploaded: (conversationId: string, filename: string) => void;
  markFileError: (conversationId: string, filename: string, errorMessage?: string) => void;
  markFileDeleting: (conversationId: string, filename: string) => void;
  removeFile: (conversationId: string, filename: string) => void;
  getConversationFileEntries: (conversationId: string) => ConversationFileEntry[];
  getFileEntry: (conversationId: string, filename: string) => ConversationFileEntry | undefined;
  setConversationUploadSession: (conversationId: string, sessionId: string) => void;
  clearConversationUploadSession: (conversationId: string) => void;
  syncConversationFiles: (conversationId: string, serverFiles: string[]) => void;
  hasUnsyncedFiles: (conversationId: string) => boolean;

  // File processing status (async upload)
  fileProcessingStatus: Record<string, { status: 'processing' | 'complete' | 'partial' | 'failed'; error?: string; startedAt?: number }>;
  setFileProcessingStatus: (conversationId: string, status: 'processing' | 'complete' | 'partial' | 'failed', error?: string) => void;
  getFileProcessingStatus: (conversationId: string) => 'processing' | 'complete' | 'partial' | 'failed' | null;
  clearFileProcessingStatus: (conversationId: string) => void;

  // Pending files for next message (input indicators)
  addPendingFileForMessage: (conversationId: string, filename: string) => void;
  removePendingFileForMessage: (conversationId: string, filename: string) => void;
  getPendingFilesForMessage: (conversationId: string) => string[];
  clearPendingFilesForMessage: (conversationId: string) => void;

  // UI actions
  setIsComposing: (composing: boolean) => void;

  // Modal actions
  openAgentsModal: () => void;
  closeAgentsModal: () => void;
  toggleAgentsModal: () => void;
  openAtAgentsModal: () => void;
  closeAtAgentsModal: () => void;
  toggleAtAgentsModal: () => void;
  openModelsModal: () => void;
  closeModelsModal: () => void;
  toggleModelsModal: () => void;
  openPromptLibraryModal: () => void;
  closePromptLibraryModal: () => void;
  openAttachModal: () => void;
  closeAttachModal: () => void;
  openProfileModal: () => void;
  closeProfileModal: () => void;
  toggleProfileModal: () => void;

  // Message editing actions
  setEditingMessage: (messageId: string) => void;
  clearEditingMessage: () => void;

  // Edit abort controller setter (shared across components)
  setEditAbortController: (controller: AbortController | null) => void;

  // Home screen conversation actions
  setHomeScreenConversation: (conversation: HomeScreenConversation) => void;
  updateHomeScreenConversation: (updates: Partial<HomeScreenConversation>) => void;
  clearHomeScreenConversation: () => void;
}
export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      // Initial state
      inputValue: '',
      selectedAgents: [],
      selectedModel: DEFAULT_MODEL,
      attachments: [],
      uploadSessionId: undefined,
      conversationInputs: {}, // Initialize conversation input tracking
      digitalMaintenanceChecks: {},
      setDigitalMaintenanceCheck: (conversationId, check) => set((state) => {
        const checks = { ...state.digitalMaintenanceChecks };
        if (check) checks[conversationId] = check;
        else delete checks[conversationId];
        return { digitalMaintenanceChecks: checks };
      }),
      conversationFiles: {}, // Initialize conversation file tracking
      fileProcessingStatus: {}, // Tracks async upload processing per conversation
      homeScreenConversation: null,
      hasGenieAccess: false,
      allowedGenieSpaceNames: [],
      isComposing: false,
      isEditingMessage: false,
      currentEditingMessageId: null,
      editAbortController: null,
      isAgentsModalOpen: false,
      isAtAgentsModalOpen: false,
      isModelsModalOpen: false,
      isPromptLibraryModalOpen: false,
      isAttachModalOpen: false,
      isProfileModalOpen: false,

      // Input actions (conversation-specific)
      setInputValue: (conversationId: string, value: string) => set({
        conversationInputs: {
          ...get().conversationInputs,
          [conversationId]: {
            ...get().conversationInputs[conversationId] || { inputValue: '', selectedAgents: [], selectedModel: DEFAULT_MODEL, characterCount: 0, messageCount: 0 },
            inputValue: value,
            characterCount: value.length
          }
        }
      }),
      setSelectedAgent: (conversationId: string, agent: string) => {
        const currentState = get().conversationInputs[conversationId] || { inputValue: '', selectedAgents: [], selectedModel: DEFAULT_MODEL, characterCount: 0, messageCount: 0 };
        set({
          conversationInputs: {
            ...get().conversationInputs,
            [conversationId]: {
              ...currentState,
              selectedAgents: [...currentState.selectedAgents, agent]
            }
          }
        });
      },
      setSelectedAgents: (conversationId: string, agents: string[]) => set({
        conversationInputs: {
          ...get().conversationInputs,
          [conversationId]: {
            ...get().conversationInputs[conversationId] || { inputValue: '', selectedAgents: [], selectedModel: DEFAULT_MODEL, characterCount: 0, messageCount: 0 },
            selectedAgents: agents
          }
        }
      }),
      addSelectedAgent: (conversationId: string, agent: string) => {
        const currentState = get().conversationInputs[conversationId] || { inputValue: '', selectedAgents: [], selectedModel: DEFAULT_MODEL, characterCount: 0, messageCount: 0 };
        set({
          conversationInputs: {
            ...get().conversationInputs,
            [conversationId]: {
              ...currentState,
              selectedAgents: [...currentState.selectedAgents, agent]
            }
          }
        });
      },
      removeSelectedAgent: (conversationId: string, agent: string) => {
        const currentState = get().conversationInputs[conversationId] || { inputValue: '', selectedAgents: [], selectedModel: DEFAULT_MODEL, characterCount: 0, messageCount: 0 };
        set({
          conversationInputs: {
            ...get().conversationInputs,
            [conversationId]: {
              ...currentState,
              selectedAgents: currentState.selectedAgents.filter(a => a !== agent)
            }
          }
        });
      },
      setSelectedModel: (conversationId: string, model: string) => {
        const normalizedModel = normalizeSelectedModel(model);
        set({
          selectedModel: normalizedModel,
          conversationInputs: {
            ...get().conversationInputs,
            [conversationId]: {
              ...get().conversationInputs[conversationId] || { inputValue: '', selectedAgents: [], selectedModel: DEFAULT_MODEL, characterCount: 0, messageCount: 0 },
              selectedModel: normalizedModel
            }
          }
        });
      },
      clearInput: (conversationId: string) => set({
        conversationInputs: {
          ...get().conversationInputs,
          [conversationId]: {
            ...get().conversationInputs[conversationId] || { inputValue: '', selectedAgents: [], selectedModel: DEFAULT_MODEL, characterCount: 0, messageCount: 0 },
            inputValue: '',
            characterCount: 0
          }
        }
      }),

      // Getter actions for conversation-specific state
      getInputValue: (conversationId: string) => get().conversationInputs[conversationId]?.inputValue || '',
      getSelectedAgents: (conversationId: string) => get().conversationInputs[conversationId]?.selectedAgents || [],
      getSelectedModel: (conversationId: string) =>
        normalizeSelectedModel(get().conversationInputs[conversationId]?.selectedModel || get().selectedModel),

      // Character count actions
      getCharacterCount: (conversationId: string) => get().conversationInputs[conversationId]?.characterCount || 0,
      setCharacterCount: (conversationId: string, count: number) => set({
        conversationInputs: {
          ...get().conversationInputs,
          [conversationId]: {
            ...get().conversationInputs[conversationId] || { inputValue: '', selectedAgents: [], selectedModel: DEFAULT_MODEL, characterCount: 0, messageCount: 0 },
            characterCount: count
          }
        }
      }),
      isCharacterCountVisible: (conversationId: string) => {
        const count = get().conversationInputs[conversationId]?.characterCount || 0;
        return count >= 300000;
      },
      isCharacterLimitExceeded: (conversationId: string) => {
        const count = get().conversationInputs[conversationId]?.characterCount || 0;
        return count > 400000;
      },

      // Message count actions (follows existing character count pattern)
      getMessageCount: (conversationId: string) => get().conversationInputs[conversationId]?.messageCount || 0,
      setMessageCount: (conversationId: string, count: number) => set({
        conversationInputs: {
          ...get().conversationInputs,
          [conversationId]: {
            ...get().conversationInputs[conversationId] || { inputValue: '', selectedAgents: [], selectedModel: DEFAULT_MODEL, characterCount: 0, messageCount: 0 },
            messageCount: count
          }
        }
      }),
      isMessageCountVisible: (conversationId: string) => {
        const count = get().conversationInputs[conversationId]?.messageCount || 0;
        return count >= 25;
      },
      isMessageLimitWarning: (conversationId: string) => {
        const count = get().conversationInputs[conversationId]?.messageCount || 0;
        return count >= 28;
      },
      isMessageLimitReached: (conversationId: string) => {
        return false;
      },
      getMessageCountStatus: (conversationId: string) => {
        const count = get().conversationInputs[conversationId]?.messageCount || 0;
        // Create a mock conversation history with the count for the utility function
        const mockHistory = Array.from({ length: count }, () => ({} as any));
        return getMessageCountStatus(mockHistory);
      },

      addAttachment: (file: File, conversationId?: string) => {
        const state = get();
        const existingAttachmentNames = state.attachments.map(a => a.file.name);
        // Only consider files from the current conversation if conversationId is provided
        const existingConversationNames = conversationId
          ? (state.conversationFiles[conversationId]?.uploadedFiles || [])
          : [];
        const existingNames = [...existingAttachmentNames, ...existingConversationNames];
        const uniqueName = normalizeFilename(existingNames, file.name);

        const fileToStore = uniqueName === file.name ? file : new File([file], uniqueName, { type: file.type });

        const attachment: ChatAttachment = {
          id: crypto.randomUUID(),
          file: fileToStore,
          uploadStatus: 'pending',
          stagingState: 'staged-for-upload', // New files are staged for upload by default
        };
        set({ attachments: [...get().attachments, attachment] });
      },

      removeAttachment: (id: string) => {
        set({ attachments: get().attachments.filter((attachment) => attachment.id !== id) });
      },

      updateAttachmentStatus: (id: string, status: ChatAttachment['uploadStatus'], progress?: number) => {
        set({
          attachments: get().attachments.map((attachment) =>
            attachment.id === id
              ? { ...attachment, uploadStatus: status, uploadProgress: progress }
              : attachment
          ),
        });
      },

      clearAttachments: () => set({ attachments: [] }),

      // Upload session management actions
      setUploadSessionId: (sessionId: string) => {
        set({ uploadSessionId: sessionId });
      },

      clearUploadSession: () => {
        set({ uploadSessionId: undefined });
      },

      markAttachmentsAsUploaded: (sessionId: string) => {
        set({
          attachments: get().attachments.map(attachment => ({
            ...attachment,
            uploadStatus: 'completed' as const,
            uploadSessionId: sessionId,
            uploadProgress: 100,
          })),
          uploadSessionId: sessionId,
        });
      },

      markAttachmentAsUploading: (fileId: string) => {
        set({
          attachments: get().attachments.map(attachment =>
            attachment.id === fileId
              ? { ...attachment, uploadStatus: 'uploading' as const, uploadProgress: 0 }
              : attachment
          ),
        });
      },

      markAttachmentAsError: (fileId: string, error: string) => {
        set({
          attachments: get().attachments.map(attachment =>
            attachment.id === fileId
              ? {
                  ...attachment,
                  uploadStatus: 'error' as const,
                  error,
                  uploadProgress: 0
                }
              : attachment
          ),
        });
      },

      // Staging state management actions
      setStagingState: (fileId: string, state: 'staged-for-upload' | 'none') => {
        set({
          attachments: get().attachments.map(attachment =>
            attachment.id === fileId
              ? { ...attachment, stagingState: state }
              : attachment
          ),
        });
      },

      getStagedForUpload: () => {
        return get().attachments.filter(attachment => attachment.stagingState === 'staged-for-upload');
      },

      hasStagedUploads: () => {
        return get().attachments.some(attachment => attachment.stagingState === 'staged-for-upload');
      },

      // Conversation-level file management actions
      addConversationFile: (conversationId: string, filename: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId] || {
          conversationId,
          uploadedFiles: [],
          pendingFiles: [],
          pendingForMessage: [],
          fileEntries: [],
        };

        // Only add if not already present
        if (!currentState.uploadedFiles.includes(filename)) {
          // Ensure unique filename within this conversation (append " (n)" if needed)
          const uniqueName = normalizeFilename(currentState.uploadedFiles || [], filename);
          set({
            conversationFiles: {
              ...state.conversationFiles,
              [conversationId]: {
                ...currentState,
                uploadedFiles: [...currentState.uploadedFiles, uniqueName],
                // Keep entries in sync
                fileEntries: (() => {
                  const entries = currentState.fileEntries || [];
                  const idx = entries.findIndex(e => e.name === filename);
                  if (idx >= 0) {
                    const updated = [...entries];
                    updated[idx] = { ...updated[idx], status: 'uploaded' };
                    return updated;
                  }
                  return [...entries, { name: filename, status: 'uploaded' }];
                })(),
                lastSyncTimestamp: Date.now(),
              }
            }
          });
        }
      },

      removeConversationFile: (conversationId: string, filename: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId];
        if (!currentState) return;

        set({
          conversationFiles: {
            ...state.conversationFiles,
            [conversationId]: {
              ...currentState,
              uploadedFiles: currentState.uploadedFiles.filter(f => f !== filename),
              fileEntries: (currentState.fileEntries || []).filter(e => e.name !== filename),
              lastSyncTimestamp: Date.now(),
            }
          }
        });
      },

      getConversationFiles: (conversationId: string) => {
        const state = get();
        const conversationState = state.conversationFiles[conversationId];
        if (!conversationState) return [];
        const deletingNames = new Set(
          (conversationState.fileEntries || [])
            .filter(e => e.status === 'deleting')
            .map(e => e.name)
        );
        const fromEntries = (conversationState.fileEntries || [])
          .map(e => e.name);
        const fromUploaded = (conversationState.uploadedFiles || []).filter(f => !deletingNames.has(f));
        const merged = new Set([...fromUploaded, ...fromEntries]);
        return Array.from(merged);
      },

      // New unified entries API
      addOrQueueFile: (conversationId: string, filename: string, origin?: 'inline' | 'modal') => {
        const state = get();
        const currentState = state.conversationFiles[conversationId] || {
          conversationId,
          uploadedFiles: [],
          pendingFiles: [],
          pendingForMessage: [],
          fileEntries: [],
        };

        const entries = currentState.fileEntries || [];
        if (!entries.find(e => e.name === filename)) {
          entries.push({ name: filename, status: 'queued', origin });
        }

        const pfm = new Set(currentState.pendingForMessage || []);
        pfm.add(filename);

        set({
          conversationFiles: {
            ...state.conversationFiles,
            [conversationId]: {
              ...currentState,
              fileEntries: [...entries],
              pendingForMessage: Array.from(pfm),
            }
          }
        });
      },

      markFileUploading: (conversationId: string, filename: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId];
        if (!currentState) return;
        const entries = currentState.fileEntries || [];
        const idx = entries.findIndex(e => e.name === filename);
        if (idx >= 0) entries[idx] = { ...entries[idx], status: 'uploading' };
        set({
          conversationFiles: {
            ...state.conversationFiles,
            [conversationId]: {
              ...currentState,
              fileEntries: [...entries],
            }
          }
        });
      },

      markFileUploaded: (conversationId: string, filename: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId];
        if (!currentState) return;
        const entries = currentState.fileEntries || [];
        const idx = entries.findIndex(e => e.name === filename);
        if (idx >= 0) entries[idx] = { ...entries[idx], status: 'uploaded', errorMessage: undefined };
        else entries.push({ name: filename, status: 'uploaded' });
        set({
          conversationFiles: {
            ...state.conversationFiles,
            [conversationId]: {
              ...currentState,
              fileEntries: [...entries],
              uploadedFiles: Array.from(new Set([...(currentState.uploadedFiles || []), filename])),
            }
          }
        });
      },

      markFileError: (conversationId: string, filename: string, errorMessage?: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId];
        if (!currentState) return;
        const entries = currentState.fileEntries || [];
        const idx = entries.findIndex(e => e.name === filename);
        if (idx >= 0) entries[idx] = { ...entries[idx], status: 'error', errorMessage };
        set({
          conversationFiles: {
            ...state.conversationFiles,
            [conversationId]: {
              ...currentState,
              fileEntries: [...entries],
            }
          }
        });
      },

      markFileDeleting: (conversationId: string, filename: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId];
        if (!currentState) return;
        const entries = currentState.fileEntries || [];
        const idx = entries.findIndex(e => e.name === filename);
        if (idx >= 0) {
          entries[idx] = { ...entries[idx], status: 'deleting' };
        } else {
          // Files restored from server data may not have a local entry yet.
          // Add one so the asynchronous completion handler can remove it.
          entries.push({ name: filename, status: 'deleting' });
        }
        set({
          conversationFiles: {
            ...state.conversationFiles,
            [conversationId]: {
              ...currentState,
              fileEntries: [...entries],
            }
          }
        });
      },

      removeFile: (conversationId: string, filename: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId];
        if (!currentState) return;
        const entries = (currentState.fileEntries || []).filter(e => e.name !== filename);
        set({
          conversationFiles: {
            ...state.conversationFiles,
            [conversationId]: {
              ...currentState,
              fileEntries: entries,
              uploadedFiles: (currentState.uploadedFiles || []).filter(f => f !== filename),
              pendingForMessage: (currentState.pendingForMessage || []).filter(f => f !== filename),

            }
          }
        });
      },

      getConversationFileEntries: (conversationId: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId];
        return currentState?.fileEntries || [];
      },

      getFileEntry: (conversationId: string, filename: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId];
        return (currentState?.fileEntries || []).find(e => e.name === filename);
      },

      setConversationUploadSession: (conversationId: string, sessionId: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId] || {
          conversationId,
          uploadedFiles: [],
          pendingFiles: [],
          pendingForMessage: [],
        };

        set({
          conversationFiles: {
            ...state.conversationFiles,
            [conversationId]: {
              ...currentState,
              lastUploadSessionId: sessionId,
              lastSyncTimestamp: Date.now(),
            }
          }
        });
      },

      clearConversationUploadSession: (conversationId: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId];
        if (!currentState) return;

        set({
          conversationFiles: {
            ...state.conversationFiles,
            [conversationId]: {
              ...currentState,
              lastUploadSessionId: undefined,
            }
          }
        });
      },

      syncConversationFiles: (conversationId: string, serverFiles: string[]) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId] || {
          conversationId,
          uploadedFiles: [],
          pendingFiles: [],
          pendingForMessage: [],
        };

        // The server is authoritative for confirmed files. getConversationFiles()
        // and getConversationFileEntries() surface fileEntries regardless of
        // uploadedFiles, so a stale entry here (e.g. 'uploaded' or 'deleting')
        // keeps showing the file even after the server confirms it's gone.
        // Drop entries the server no longer reports, unless they're still a
        // local-only in-flight operation (queued/uploading) that hasn't been
        // confirmed by the server yet.
        const serverFileSet = new Set(serverFiles);
        // A newly uploaded file is not included in conversationHistory until it
        // is associated with a sent message. Conversation-detail refetches that
        // land while processing is underway therefore contain only older,
        // message-associated files. Keep files explicitly pending for the next
        // message until the send flow either associates or removes them.
        const pendingForMessageSet = new Set(currentState.pendingForMessage || []);
        const reconciledEntries = (currentState.fileEntries || []).filter(e =>
          serverFileSet.has(e.name) ||
          pendingForMessageSet.has(e.name) ||
          e.status === 'queued' ||
          e.status === 'uploading'
        );

        set({
          conversationFiles: {
            ...state.conversationFiles,
            [conversationId]: {
              ...currentState,
              uploadedFiles: serverFiles,
              fileEntries: reconciledEntries,
              lastSyncTimestamp: Date.now(),
            }
          }
        });
      },

      hasUnsyncedFiles: (conversationId: string) => {
        const state = get();
        const conversationState = state.conversationFiles[conversationId];
        if (!conversationState) return false;

        // Check if there are any local files that might not be synced
        // This is a simple heuristic - could be enhanced with more sophisticated logic
        return conversationState.uploadedFiles.length > 0 &&
               (!conversationState.lastSyncTimestamp ||
                Date.now() - conversationState.lastSyncTimestamp > 60000); // 1 minute threshold
      },

      // File processing status management (async upload)
      setFileProcessingStatus: (conversationId: string, status: 'processing' | 'complete' | 'partial' | 'failed', error?: string) => {
        set((state) => ({
          fileProcessingStatus: {
            ...state.fileProcessingStatus,
            [conversationId]: {
              status,
              error,
              startedAt: status === 'processing' ? Date.now() : state.fileProcessingStatus[conversationId]?.startedAt,
            },
          },
        }));
      },

      getFileProcessingStatus: (conversationId: string) => {
        const entry = get().fileProcessingStatus[conversationId];
        return entry?.status ?? null;
      },

      clearFileProcessingStatus: (conversationId: string) => {
        set((state) => {
          const { [conversationId]: _, ...rest } = state.fileProcessingStatus;
          return { fileProcessingStatus: rest };
        });
      },

      // Pending files for next message management actions
      addPendingFileForMessage: (conversationId: string, filename: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId] || {
          conversationId,
          uploadedFiles: [],
          pendingFiles: [],
          pendingForMessage: [],
        };

        // Only add if not already present
        if (!currentState.pendingForMessage.includes(filename)) {
          set({
            conversationFiles: {
              ...state.conversationFiles,
              [conversationId]: {
                ...currentState,
                pendingForMessage: [...currentState.pendingForMessage, filename],
              }
            }
          });
        }
      },

      removePendingFileForMessage: (conversationId: string, filename: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId];
        if (!currentState) return;

        set({
          conversationFiles: {
            ...state.conversationFiles,
            [conversationId]: {
              ...currentState,
              pendingForMessage: currentState.pendingForMessage.filter(f => f !== filename),
            }
          }
        });
      },

      getPendingFilesForMessage: (conversationId: string) => {
        const state = get();
        const conversationState = state.conversationFiles[conversationId];
        return conversationState?.pendingForMessage || [];
      },

      clearPendingFilesForMessage: (conversationId: string) => {
        const state = get();
        const currentState = state.conversationFiles[conversationId];
        if (!currentState) return;

        set({
          conversationFiles: {
            ...state.conversationFiles,
            [conversationId]: {
              ...currentState,
              pendingForMessage: [],
            }
          }
        });
      },

      // UI actions
      setIsComposing: (composing: boolean) => set({ isComposing: composing }),

      // Genie access actions
      setGenieAccess: (hasAccess: boolean) => set({ hasGenieAccess: hasAccess }),
      setAllowedGenieSpaceNames: (spaceNames: string[]) => set({ allowedGenieSpaceNames: spaceNames }),

      // Modal actions
      openAgentsModal: () => set({ isAgentsModalOpen: true }),
      closeAgentsModal: () => set({ isAgentsModalOpen: false }),
      openAtAgentsModal: () => set({ isAtAgentsModalOpen: true }),
      closeAtAgentsModal: () => set({ isAtAgentsModalOpen: false }),
      toggleAgentsModal: () => set((state) => ({ isAgentsModalOpen: !state.isAgentsModalOpen })),
      toggleAtAgentsModal: () => set((state) => ({ isAtAgentsModalOpen: !state.isAtAgentsModalOpen })),
      openModelsModal: () => set({ isModelsModalOpen: true }),
      closeModelsModal: () => set({ isModelsModalOpen: false }),
      toggleModelsModal: () => set((state) => ({ isModelsModalOpen: !state.isModelsModalOpen })),
      openPromptLibraryModal: () => set({ isPromptLibraryModalOpen: true }),
      closePromptLibraryModal: () => set({ isPromptLibraryModalOpen: false }),
      openAttachModal: () => set({ isAttachModalOpen: true }),
      closeAttachModal: () => set({ isAttachModalOpen: false }),
      openProfileModal: () => set({ isProfileModalOpen: true }),
      closeProfileModal: () => set({ isProfileModalOpen: false }),
      toggleProfileModal: () => set((state) => ({ isProfileModalOpen: !state.isProfileModalOpen })),

      // Message editing actions
      setEditingMessage: (messageId: string) => set({
        isEditingMessage: true,
        currentEditingMessageId: messageId
      }),
      clearEditingMessage: () => set({
        isEditingMessage: false,
        currentEditingMessageId: null
      }),

      // Shared edit abort controller
      setEditAbortController: (controller: AbortController | null) => set({
        editAbortController: controller
      }),

      // Home screen conversation actions
      setHomeScreenConversation: (conversation: HomeScreenConversation) =>
        set({ homeScreenConversation: conversation }),

      updateHomeScreenConversation: (updates: Partial<HomeScreenConversation>) => {
        const current = get().homeScreenConversation;
        if (current) {
          set({
            homeScreenConversation: { ...current, ...updates } as HomeScreenConversation
          });
        }
      },

      clearHomeScreenConversation: () => set({ homeScreenConversation: null }),
    }),
    {
      name: 'chat-storage',
      version: 6,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        selectedAgents: state.selectedAgents,
        selectedModel: state.selectedModel,
        hasGenieAccess: state.hasGenieAccess,
        allowedGenieSpaceNames: state.allowedGenieSpaceNames,
        // Don't persist inputValue, attachments, or isComposing (session-specific state)
      }),
      migrate: (persistedState: any, _version: number) => {
        // Reconcile any older persisted model selection against the current
        // selectable model list. Unsupported values fall back to DEFAULT_MODEL.
        if (persistedState?.selectedModel) {
          persistedState.selectedModel = normalizeSelectedModel(persistedState.selectedModel);
        }
        return persistedState;
      },
    }
  )
);

/**
 * Utility selectors for client state management
 * Simplified to focus on UI state only
 */
export const useChatSelectors = {
  // Input selectors
  canSendMessage: () => useChatStore(state =>
    state.inputValue.trim().length > 0 && !state.isComposing
  ),
  hasAttachments: () => useChatStore(state => state.attachments.length > 0),
  hasValue: () => useChatStore(state => state.inputValue.trim().length > 0),

  // Attachment selectors
  attachmentCount: () => useChatStore(state => state.attachments.length),
  pendingAttachments: () => useChatStore(state =>
    state.attachments.filter(a => a.uploadStatus === 'pending')
  ),
  uploadingAttachments: () => useChatStore(state =>
    state.attachments.filter(a => a.uploadStatus === 'uploading')
  ),
  completedAttachments: () => useChatStore(state =>
    state.attachments.filter(a => a.uploadStatus === 'completed')
  ),
  failedAttachments: () => useChatStore(state =>
    state.attachments.filter(a => a.uploadStatus === 'error')
  ),

  // Staging selectors
  stagedForUpload: () => useChatStore(state =>
    state.attachments.filter(a => a.stagingState === 'staged-for-upload')
  ),
  hasStagedUploads: () => useChatStore(state => state.hasStagedUploads()),

  // Genie access selectors
  hasGenieAccess: () => useChatStore(state => state.hasGenieAccess),
  getAllowedGenieSpaceNames: () => useChatStore(state => state.allowedGenieSpaceNames),

  // Home screen conversation selectors
  hasHomeScreenConversation: () => useChatStore(state =>
    state.homeScreenConversation !== null
  ),
  isHomeScreenConversationLoading: () => false, // Removed isLoading field - handled differently now
  homeScreenConversationError: () => useChatStore(state =>
    state.homeScreenConversation?.error
  ),
};

// Export types for use in components
export type { ChatState, ChatAttachment, HomeScreenConversation, ConversationFileState, ConversationFileEntry };
